import PolicyLayout, { PolicySection, P, UL, LI, Callout } from "./PolicyLayout";

const TOC = [
  { id: "principles", label: "Our principles" },
  { id: "respect", label: "Respect" },
  { id: "honesty", label: "Honesty" },
  { id: "safety", label: "Physical safety" },
  { id: "privacy", label: "Privacy in sessions" },
  { id: "not-tolerated", label: "What's not tolerated" },
  { id: "reporting", label: "How to report" },
  { id: "what-we-do", label: "What we do about reports" },
  { id: "in-emergency", label: "If you're in danger right now" },
  { id: "contact", label: "Contact" },
];

export default function TrustSafety() {
  return (
    <PolicyLayout
      eyebrow="Trust & safety"
      title="Trust & Safety / Community Policy"
      updated="February 2026"
      intro="Coachly is a marketplace of real humans meeting real humans, sometimes in person. Trust is the whole product. Here's how we protect it."
      toc={TOC}
      testId="policy-trust-safety"
    >
      <PolicySection id="principles" title="1. Our principles">
        <UL>
          <LI><strong>Everyone deserves respect</strong> — regardless of race, ethnicity, national origin, religion, gender identity, sexual orientation, disability, age, or body.</LI>
          <LI><strong>Honesty scales trust.</strong> No fake profiles, no fake reviews, no fake outcomes.</LI>
          <LI><strong>Safety is non-negotiable</strong> — physical, financial, and emotional.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="respect" title="2. Respect">
        <P>
          Communicate the way you'd want to be communicated with. Feedback in a session
          should be direct but never cruel or personal. Disagreements about training
          approach are fine — insults or slurs are not.
        </P>
      </PolicySection>

      <PolicySection id="honesty" title="3. Honesty">
        <UL>
          <LI>Only list certifications and experience you actually have.</LI>
          <LI>Reviews must describe a real session with the coach you booked through Coachly.</LI>
          <LI>Don't offer or accept rewards for fake reviews.</LI>
          <LI>Don't manipulate ratings by asking friends or family to review your profile.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="safety" title="4. Physical safety">
        <UL>
          <LI>Follow the safety rules of your discipline — warm-ups, spotters, protective gear, allergen checks, etc.</LI>
          <LI>Do a quick health/injury intake for physical sessions.</LI>
          <LI>Choose in-person locations that are safe and public (parks, gyms, studios). Avoid private residences until both parties have met before.</LI>
          <LI>Coaches: if a session becomes unsafe, stop and mark the session complete or cancelled with notes for our support team.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="privacy" title="5. Privacy in sessions">
        <UL>
          <LI>Don't record a session — audio or video — unless both parties agree in writing (an in-app message counts).</LI>
          <LI>What you're told in a session stays between the two of you unless there's a safety concern.</LI>
          <LI>Never share a client's contact information with a third party.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="not-tolerated" title="6. What's not tolerated">
        <Callout tone="amber">
          Any of the following can lead to <strong>immediate account removal</strong>{" "}
          and, where appropriate, referral to law enforcement.
        </Callout>
        <UL>
          <LI>Sexual solicitation, sexual harassment, or non-consensual contact of any kind.</LI>
          <LI>Physical threats, stalking, or intimidation on- or off-platform.</LI>
          <LI>Hate speech or discrimination targeting a protected class.</LI>
          <LI>Coaching under the influence of alcohol or unlawful substances.</LI>
          <LI>Sharing sexual images, or asking a client or coach to share them.</LI>
          <LI>Selling illegal services — unregulated medical treatments, banned substances, or anything requiring a license you don't hold.</LI>
          <LI>Financial fraud — chargeback abuse, fake bookings, or systematic off-platform payments to avoid fees.</LI>
          <LI>Circumventing a suspension by creating a new account.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="reporting" title="7. How to report">
        <UL>
          <LI>Use the <strong>Report</strong> button on any coach profile or review.</LI>
          <LI>Email <a href="mailto:safety@coachly.app" className="text-[#2563EB] hover:underline">safety@coachly.app</a> with as much detail as you can — dates, booking references, screenshots.</LI>
          <LI>You can report anonymously if you prefer. We will follow up if we need clarification and if you left contact info.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="what-we-do" title="8. What we do about reports">
        <UL>
          <LI>Every report gets a first look within <strong>1 business day</strong>.</LI>
          <LI>Most investigations close within 3 business days.</LI>
          <LI>Severe safety reports may result in immediate profile hiding while we investigate.</LI>
          <LI>We tell the reporter what action we took, without disclosing personal details of the other party.</LI>
        </UL>
      </PolicySection>

      <PolicySection id="in-emergency" title="9. If you're in immediate danger">
        <Callout tone="amber">
          Coachly is not an emergency service. If you or someone else is in immediate
          danger, call <strong>911</strong> (US) or your local emergency number. Then, if
          you can, email <a href="mailto:safety@coachly.app" className="underline">safety@coachly.app</a> so
          we can act on our side.
        </Callout>
      </PolicySection>

      <PolicySection id="contact" title="10. Contact">
        <P>
          Trust &amp; Safety team:{" "}
          <a href="mailto:safety@coachly.app" className="text-[#2563EB] hover:underline">safety@coachly.app</a>{" "}
          — responses within one business day.
        </P>
      </PolicySection>
    </PolicyLayout>
  );
}
